import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { Popover } from '@openng/optimus-ui/popover';

@Component({
  selector: 'ui-popover',
  imports: [Popover],
  standalone: true,
  templateUrl: './ui-popover.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiPopoverComponent {
  @ViewChild('popover') private popover!: Popover;

  /** The trigger the open panel points at, kept for `centerArrow`. */
  private target: HTMLElement | null = null;

  show(target: HTMLElement): void {
    this.target = target;
    this.popover.hide();
    setTimeout(() => this.popover.show(null, target));
  }

  hide(): void {
    this.popover.hide();
  }

  /**
   * Aim the arrow at the centre of the trigger.
   *
   * The library points it at the trigger's left edge, and measures the panel
   * while its scale-in animation is still running, so on a narrow trigger
   * (an icon button) the arrow lands well left of the centre. Runs on the
   * popover's `onShow`, right after the library has aligned the panel: the
   * settled, untransformed `offsetLeft` is already in place there.
   */
  centerArrow(): void {
    const container = this.popover.container;
    const target = this.target;
    if (!container || !target) return;

    const targetRect = target.getBoundingClientRect();
    const width = container.offsetWidth;
    const scrollX = window.scrollX;
    const triggerLeft = targetRect.left + scrollX;
    const triggerRight = triggerLeft + targetRect.width;

    // A panel crossing the viewport's right edge is right-aligned with the
    // trigger, and so is one opened from a trigger wider than itself (the
    // account block with the name): keep both under the trigger's right edge.
    // Redo the placement with the settled width: the library read it
    // mid-animation, when the projected content made it transiently wider,
    // and left the panel short of the trigger's edge.
    if (
      targetRect.width > width ||
      triggerLeft + width > document.documentElement.clientWidth
    ) {
      const left = Math.max(0, triggerRight - width);
      container.style.insetInlineStart = `${left}px`;
    }

    // The arrow is placed at `--p-popover-arrow-left + arrow-offset`, so with
    // the variable at zero the computed left is the theme's offset itself.
    container.style.setProperty('--p-popover-arrow-left', '0px');
    const arrowOffset =
      parseFloat(getComputedStyle(container, '::before').left) || 0;

    const parent = container.offsetParent as HTMLElement | null;
    const containerLeft = parent
      ? parent.getBoundingClientRect().left + container.offsetLeft
      : container.getBoundingClientRect().left;
    const arrowLeft =
      targetRect.left + targetRect.width / 2 - containerLeft - arrowOffset;

    container.style.setProperty('--p-popover-arrow-left', `${arrowLeft}px`);
  }
}
